// 推荐算法引擎实现

import type {
  BookInfo,
  Recommendation,
  RecommendationType
} from '@/types/reading';

import { UserBehaviorTracker, type UserPreferenceProfile } from './userBehaviorTracker';
import { BookLibrary } from './bookLibrary';
import { BookSearchManager } from './bookSearchManager';
import type { StorageManager } from './storage';

export interface RecommendationConfig {
  maxRecommendations: number;
  diversityFactor: number; // 0-1, 推荐多样性
  noveltyFactor: number; // 0-1, 新颖性权重
  popularityFactor: number; // 0-1, 流行度权重
  personalityFactor: number; // 0-1, 个性化权重
  excludeReadBooks: boolean;
  minRating: number;
}

export interface RecommendationScore {
  bookId: string;
  totalScore: number;
  scores: {
    similarity: number;
    popularity: number;
    novelty: number;
    preference: number;
    quality: number;
  };
  reasons: string[];
}

export class RecommendationEngine {
  private behaviorTracker: UserBehaviorTracker;
  private bookLibrary: BookLibrary;
  private searchManager: BookSearchManager;
  private storage: StorageManager;
  private defaultConfig: RecommendationConfig = {
    maxRecommendations: 20,
    diversityFactor: 0.3,
    noveltyFactor: 0.2,
    popularityFactor: 0.3,
    personalityFactor: 0.5,
    excludeReadBooks: true,
    minRating: 3.0
  };

  constructor(
    behaviorTracker: UserBehaviorTracker,
    bookLibrary: BookLibrary,
    searchManager: BookSearchManager,
    storage: StorageManager
  ) {
    this.behaviorTracker = behaviorTracker;
    this.bookLibrary = bookLibrary;
    this.searchManager = searchManager;
    this.storage = storage;
  }

  // ============================================================================
  // 基于偏好的图书推荐
  // ============================================================================

  async getPersonalizedRecommendations(
    config?: Partial<RecommendationConfig>
  ): Promise<Recommendation[]> {
    try {
      const finalConfig = { ...this.defaultConfig, ...config };
      
      // 1. 获取用户偏好档案
      const userProfile = await this.behaviorTracker.analyzeUserBehavior();
      
      // 2. 获取候选图书
      const candidateBooks = await this.getCandidateBooks(finalConfig);
      
      // 3. 计算推荐分数
      const scoredBooks = await this.scoreBooks(candidateBooks, userProfile, finalConfig);
      
      // 4. 应用多样性和过滤
      const diversifiedBooks = this.applyDiversification(scoredBooks, finalConfig);
      
      // 5. 生成推荐结果
      const recommendations = await this.generateRecommendations(
        diversifiedBooks.slice(0, finalConfig.maxRecommendations),
        userProfile
      );

      // 6. 记录推荐结果
      await this.recordRecommendations(recommendations);

      return recommendations;

    } catch (error) {
      console.error('Get personalized recommendations failed:', error);
      return [];
    }
  }

  private async getCandidateBooks(config: RecommendationConfig): Promise<BookInfo[]> {
    try {
      // 获取图书库中的图书
      const libraryBooks = await this.bookLibrary.getLibraryBooks();
      
      // 获取热门图书
      const popularBooks = await this.searchManager.getPopularBooks(100);
      
      // 合并去重
      const allBooks = this.deduplicateBooks([...libraryBooks, ...popularBooks]);
      
      // 过滤已读图书
      if (config.excludeReadBooks) {
        const readBooks = await this.getReadBooks();
        const readBookIds = new Set(readBooks);
        return allBooks.filter(book => !readBookIds.has(book.id));
      }

      return allBooks;

    } catch (error) {
      console.error('Get candidate books failed:', error);
      return [];
    }
  }

  private async getReadBooks(): Promise<string[]> {
    try {
      const completedBooks = await this.storage.getUserData('completed_books') || [];
      const readingHistory = await this.storage.getUserData('reading_history') || [];
      
      const readBookIds = new Set<string>();
      
      // 添加已完成的图书
      completedBooks.forEach((bookId: string) => readBookIds.add(bookId));
      
      // 添加有阅读记录的图书
      readingHistory.forEach((entry: any) => {
        if (entry.bookId) {
          readBookIds.add(entry.bookId);
        }
      });

      return Array.from(readBookIds);

    } catch (error) {
      console.error('Get read books failed:', error);
      return [];
    }
  }

  private async scoreBooks(
    books: BookInfo[],
    userProfile: UserPreferenceProfile,
    config: RecommendationConfig
  ): Promise<RecommendationScore[]> {
    const scoredBooks: RecommendationScore[] = [];

    for (const book of books) {
      const scores = {
        similarity: this.calculateSimilarityScore(book, userProfile),
        popularity: this.calculatePopularityScore(book),
        novelty: this.calculateNoveltyScore(book),
        preference: this.calculatePreferenceScore(book, userProfile),
        quality: this.calculateQualityScore(book)
      };

      const totalScore = 
        scores.similarity * 0.3 +
        scores.popularity * config.popularityFactor +
        scores.novelty * config.noveltyFactor +
        scores.preference * config.personalityFactor +
        scores.quality * 0.2;

      const reasons = this.generateScoreReasons(book, scores, userProfile);

      scoredBooks.push({
        bookId: book.id,
        totalScore,
        scores,
        reasons
      });
    }

    return scoredBooks.sort((a, b) => b.totalScore - a.totalScore);
  }

  private calculateSimilarityScore(book: BookInfo, userProfile: UserPreferenceProfile): number {
    let score = 0;

    // 分类相似度
    const categoryMatch = userProfile.favoriteCategories.find(
      cat => cat.category.toLowerCase() === book.category.toLowerCase()
    );
    if (categoryMatch) {
      score += categoryMatch.score * 0.4;
    }

    // 作者相似度
    const authorMatch = userProfile.favoriteAuthors.find(
      author => author.author.toLowerCase() === book.author.toLowerCase()
    );
    if (authorMatch) {
      score += authorMatch.score * 0.3;
    }

    // 标签相似度
    const tagMatches = book.tags.filter(tag =>
      userProfile.favoriteGenres.some(genre => 
        genre.genre.toLowerCase() === tag.toLowerCase()
      )
    );
    score += tagMatches.length * 0.1;

    return Math.min(score, 1.0);
  }

  private calculatePopularityScore(book: BookInfo): number {
    // 基于评分、字数、章节数等计算流行度
    let score = 0;

    if (book.rating) {
      score += (book.rating / 5) * 0.4;
    }

    if (book.wordCount) {
      // 字数适中的图书得分更高
      const idealWordCount = 500000; // 50万字
      const wordCountScore = 1 - Math.abs(book.wordCount - idealWordCount) / idealWordCount;
      score += Math.max(0, wordCountScore) * 0.3;
    }

    if (book.chapters.length > 0) {
      // 章节数适中的图书得分更高
      const idealChapterCount = 100;
      const chapterScore = 1 - Math.abs(book.chapters.length - idealChapterCount) / idealChapterCount;
      score += Math.max(0, chapterScore) * 0.3;
    }

    return Math.min(score, 1.0);
  }

  private calculateNoveltyScore(book: BookInfo): number {
    // 基于更新时间计算新颖性
    const now = Date.now();
    const bookTime = book.updateTime.getTime();
    const daysSinceUpdate = (now - bookTime) / (24 * 60 * 60 * 1000);

    // 越新的图书得分越高，但不能太新（可能内容不够）
    if (daysSinceUpdate < 7) {
      return 0.5; // 太新的图书降低分数
    } else if (daysSinceUpdate < 30) {
      return 1.0; // 最近一个月的图书得分最高
    } else if (daysSinceUpdate < 90) {
      return 0.8; // 最近三个月的图书得分较高
    } else if (daysSinceUpdate < 365) {
      return 0.6; // 最近一年的图书得分中等
    } else {
      return 0.3; // 超过一年的图书得分较低
    }
  }

  private calculatePreferenceScore(book: BookInfo, userProfile: UserPreferenceProfile): number {
    let score = 0;

    // 图书长度偏好
    if (userProfile.contentPreferences.preferredBookLength === 'short' && book.wordCount && book.wordCount < 200000) {
      score += 0.3;
    } else if (userProfile.contentPreferences.preferredBookLength === 'medium' && book.wordCount && book.wordCount >= 200000 && book.wordCount <= 800000) {
      score += 0.3;
    } else if (userProfile.contentPreferences.preferredBookLength === 'long' && book.wordCount && book.wordCount > 800000) {
      score += 0.3;
    }

    // 更新状态偏好
    if (userProfile.contentPreferences.preferredUpdateFrequency === 'completed' && book.status === 'completed') {
      score += 0.2;
    } else if (userProfile.contentPreferences.preferredUpdateFrequency === 'ongoing' && book.status === 'ongoing') {
      score += 0.2;
    }

    // 语言偏好
    if (book.language && userProfile.contentPreferences.languagePreference.includes(book.language)) {
      score += 0.2;
    }

    return Math.min(score, 1.0);
  }

  private calculateQualityScore(book: BookInfo): number {
    let score = 0;

    // 评分质量
    if (book.rating) {
      score += (book.rating / 5) * 0.5;
    }

    // 内容完整性
    if (book.description && book.description.length > 50) {
      score += 0.2;
    }

    if (book.coverUrl) {
      score += 0.1;
    }

    if (book.chapters.length > 10) {
      score += 0.2;
    }

    return Math.min(score, 1.0);
  }

  private generateScoreReasons(
    book: BookInfo,
    scores: RecommendationScore['scores'],
    _userProfile: UserPreferenceProfile
  ): string[] {
    const reasons: string[] = [];

    if (scores.preference > 0.5) {
      const categoryMatch = _userProfile.favoriteCategories.find(
        cat => cat.category.toLowerCase() === book.category.toLowerCase()
      );
      if (categoryMatch) {
        reasons.push(`您喜欢${book.category}类图书`);
      }

      const authorMatch = _userProfile.favoriteAuthors.find(
        author => author.author.toLowerCase() === book.author.toLowerCase()
      );
      if (authorMatch) {
        reasons.push(`您喜欢${book.author}的作品`);
      }
    }

    if (scores.popularity > 0.7) {
      reasons.push('热门推荐');
    }

    if (scores.quality > 0.7) {
      reasons.push('高质量内容');
    }

    if (scores.novelty > 0.8) {
      reasons.push('最新更新');
    }

    return reasons;
  }

  // ============================================================================
  // 热门和新书推荐
  // ============================================================================

  async getPopularRecommendations(limit: number = 10): Promise<Recommendation[]> {
    try {
      const popularBooks = await this.searchManager.getPopularBooks(limit * 2);
      
      // 过滤已读图书
      const readBooks = await this.getReadBooks();
      const readBookIds = new Set(readBooks);
      const unreadBooks = popularBooks.filter(book => !readBookIds.has(book.id));

      const recommendations: Recommendation[] = unreadBooks.slice(0, limit).map(book => ({
        bookId: book.id,
        score: this.calculatePopularityScore(book),
        reason: {
          type: 'popular' as RecommendationType,
          description: '热门图书推荐',
          factors: ['高评分', '热门阅读', '用户推荐']
        },
        timestamp: new Date()
      }));

      await this.recordRecommendations(recommendations);
      return recommendations;

    } catch (error) {
      console.error('Get popular recommendations failed:', error);
      return [];
    }
  }

  async getNewReleaseRecommendations(limit: number = 10): Promise<Recommendation[]> {
    try {
      // 获取最近更新的图书
      const allBooks = await this.bookLibrary.getLibraryBooks();
      
      // 按更新时间排序
      const recentBooks = allBooks
        .sort((a, b) => b.updateTime.getTime() - a.updateTime.getTime())
        .slice(0, limit * 2);

      // 过滤已读图书
      const readBooks = await this.getReadBooks();
      const readBookIds = new Set(readBooks);
      const unreadBooks = recentBooks.filter(book => !readBookIds.has(book.id));

      const recommendations: Recommendation[] = unreadBooks.slice(0, limit).map(book => ({
        bookId: book.id,
        score: this.calculateNoveltyScore(book),
        reason: {
          type: 'new_release' as RecommendationType,
          description: '最新图书推荐',
          factors: ['最新更新', '新鲜内容']
        },
        timestamp: new Date()
      }));

      await this.recordRecommendations(recommendations);
      return recommendations;

    } catch (error) {
      console.error('Get new release recommendations failed:', error);
      return [];
    }
  }

  async getSimilarBooksRecommendations(bookId: string, limit: number = 10): Promise<Recommendation[]> {
    try {
      const targetBook = await this.searchManager.getBookDetail(bookId);
      if (!targetBook) {
        return [];
      }

      // 获取候选图书
      const candidateBooks = await this.getCandidateBooks(this.defaultConfig);
      
      // 计算相似度
      const similarBooks = candidateBooks
        .filter(book => book.id !== bookId)
        .map(book => ({
          book,
          similarity: this.calculateBookSimilarity(targetBook, book)
        }))
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, limit);

      const recommendations: Recommendation[] = similarBooks.map(({ book, similarity }) => ({
        bookId: book.id,
        score: similarity,
        reason: {
          type: 'similar_books' as RecommendationType,
          description: `与《${targetBook.title}》相似`,
          factors: this.getSimilarityFactors(targetBook, book)
        },
        timestamp: new Date()
      }));

      await this.recordRecommendations(recommendations);
      return recommendations;

    } catch (error) {
      console.error('Get similar books recommendations failed:', error);
      return [];
    }
  }

  private calculateBookSimilarity(book1: BookInfo, book2: BookInfo): number {
    let similarity = 0;

    // 分类相似度
    if (book1.category.toLowerCase() === book2.category.toLowerCase()) {
      similarity += 0.4;
    }

    // 作者相似度
    if (book1.author.toLowerCase() === book2.author.toLowerCase()) {
      similarity += 0.3;
    }

    // 标签相似度
    const commonTags = book1.tags.filter(tag1 =>
      book2.tags.some(tag2 => tag1.toLowerCase() === tag2.toLowerCase())
    );
    similarity += (commonTags.length / Math.max(book1.tags.length, book2.tags.length, 1)) * 0.2;

    // 字数相似度
    if (book1.wordCount && book2.wordCount) {
      const wordCountDiff = Math.abs(book1.wordCount - book2.wordCount);
      const maxWordCount = Math.max(book1.wordCount, book2.wordCount);
      const wordCountSimilarity = 1 - (wordCountDiff / maxWordCount);
      similarity += wordCountSimilarity * 0.1;
    }

    return Math.min(similarity, 1.0);
  }

  private getSimilarityFactors(book1: BookInfo, book2: BookInfo): string[] {
    const factors: string[] = [];

    if (book1.category.toLowerCase() === book2.category.toLowerCase()) {
      factors.push(`同属${book1.category}类`);
    }

    if (book1.author.toLowerCase() === book2.author.toLowerCase()) {
      factors.push(`同一作者：${book1.author}`);
    }

    const commonTags = book1.tags.filter(tag1 =>
      book2.tags.some(tag2 => tag1.toLowerCase() === tag2.toLowerCase())
    );
    if (commonTags.length > 0) {
      factors.push(`相似标签：${commonTags.join(', ')}`);
    }

    return factors;
  }

  // ============================================================================
  // 推荐理由说明
  // ============================================================================

  private async generateRecommendations(
    scoredBooks: RecommendationScore[],
    userProfile: UserPreferenceProfile
  ): Promise<Recommendation[]> {
    const recommendations: Recommendation[] = [];

    for (const scoredBook of scoredBooks) {
      const recommendationType = this.determineRecommendationType(scoredBook, userProfile);
      
      const recommendation: Recommendation = {
        bookId: scoredBook.bookId,
        score: scoredBook.totalScore,
        reason: {
          type: recommendationType,
          description: this.generateRecommendationDescription(recommendationType, scoredBook),
          factors: scoredBook.reasons
        },
        timestamp: new Date()
      };

      recommendations.push(recommendation);
    }

    return recommendations;
  }

  private determineRecommendationType(
    _scoredBook: RecommendationScore,
    _userProfile: UserPreferenceProfile
  ): RecommendationType {
    if (_scoredBook.scores.preference > 0.7) {
      return 'user_preference';
    } else if (_scoredBook.scores.popularity > 0.7) {
      return 'popular';
    } else if (_scoredBook.scores.novelty > 0.8) {
      return 'new_release';
    } else if (_scoredBook.scores.similarity > 0.6) {
      return 'similar_books';
    } else {
      return 'popular';
    }
  }

  private generateRecommendationDescription(
    type: RecommendationType,
    _scoredBook: RecommendationScore
  ): string {
    switch (type) {
      case 'user_preference':
        return '根据您的阅读偏好推荐';
      case 'popular':
        return '热门图书推荐';
      case 'new_release':
        return '最新图书推荐';
      case 'similar_books':
        return '相似图书推荐';
      case 'same_author':
        return '同作者作品推荐';
      case 'same_category':
        return '同类图书推荐';
      default:
        return '为您推荐';
    }
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private deduplicateBooks(books: BookInfo[]): BookInfo[] {
    const seen = new Set<string>();
    return books.filter(book => {
      const key = `${book.title.toLowerCase()}_${book.author.toLowerCase()}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private applyDiversification(
    scoredBooks: RecommendationScore[],
    config: RecommendationConfig
  ): RecommendationScore[] {
    if (config.diversityFactor === 0) {
      return scoredBooks;
    }

    const diversified: RecommendationScore[] = [];

    for (const scoredBook of scoredBooks) {
      // 这里需要获取图书信息来检查分类和作者
      // 暂时跳过多样性处理
      diversified.push(scoredBook);
    }

    return diversified;
  }

  private async recordRecommendations(recommendations: Recommendation[]): Promise<void> {
    try {
      const existingRecommendations = await this.storage.getUserData('recommendations') || [];
      const allRecommendations = [...existingRecommendations, ...recommendations];
      
      // 保持最近的推荐记录
      const maxRecommendations = 1000;
      const recentRecommendations = allRecommendations
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, maxRecommendations);

      await this.storage.saveUserData('recommendations', recentRecommendations);
    } catch (error) {
      console.error('Record recommendations failed:', error);
    }
  }

  async getRecommendationHistory(limit: number = 50): Promise<Recommendation[]> {
    try {
      const recommendations = await this.storage.getUserData('recommendations') || [];
      return recommendations
        .sort((a: Recommendation, b: Recommendation) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, limit);
    } catch (error) {
      console.error('Get recommendation history failed:', error);
      return [];
    }
  }

  async clearRecommendationHistory(): Promise<void> {
    try {
      await this.storage.saveUserData('recommendations', []);
    } catch (error) {
      console.error('Clear recommendation history failed:', error);
    }
  }
}